import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Flex,
  Text,
  IconButton,
  useColorModeValue,
  Grid,
  GridItem,
  Badge,
  Textarea,
  Button,
  useToast,
  keyframes,
} from 'components-lib';
import { FaMicrophone, FaStop, FaTrash, FaEdit, FaPause, FaPlay } from 'react-icons/fa';

// Define pulse animation
const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const VoiceInputSection = ({ title, type, initialData = {}, hideTextarea = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [status, setStatus] = useState('idle');
  const [data, setData] = useState(initialData);
  const [transcript, setTranscript] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [pausedTranscript, setPausedTranscript] = useState('');
  const [renderCount, setRenderCount] = useState(0);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const toast = useToast();

  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);

  // Debug log every render
  console.log(`=== ${title} COMPONENT RENDER ===`, {
    isRecording,
    transcript: `"${transcript}"`,
    finalTranscript: `"${finalTranscript}"`,
    status,
    type
  });

  // Initialize speech recognition only once
  useEffect(() => {
    const initializeSpeechRecognition = () => {
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();

        const recognition = recognitionRef.current;
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
          console.log('Speech recognition started');
          isListeningRef.current = true;
          setStatus('listening');
        };

        recognition.onresult = (event) => {
          console.log('Speech recognition result:', event);
          let interimTranscript = '';
          let finalTranscriptText = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
              finalTranscriptText += result[0].transcript;
            } else {
              interimTranscript += result[0].transcript;
            }
          }

          console.log('Interim:', interimTranscript, 'Final:', finalTranscriptText);

          // Force immediate re-render
          flushSync(() => {
            setTranscript(interimTranscript);
            if (finalTranscriptText) {
              setFinalTranscript(prev => prev + finalTranscriptText + ' ');
            }
            setRenderCount(prev => prev + 1);
          });
        };

        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsRecording(false);
          setStatus('idle');
          isListeningRef.current = false;

          let errorMessage = 'Speech recognition error occurred.';
          if (event.error === 'not-allowed') {
            errorMessage = 'Microphone access denied. Please allow microphone access and try again.';
          } else if (event.error === 'no-speech') {
            errorMessage = 'No speech detected. Please try speaking again.';
          }

          toast({
            title: 'Speech Recognition Error',
            description: errorMessage,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        };

        recognition.onend = () => {
          console.log('Speech recognition ended');
          isListeningRef.current = false;
          if (isRecording) {
            // Restart if still recording
            try {
              recognition.start();
            } catch (error) {
              console.error('Error restarting recognition:', error);
              setIsRecording(false);
              setStatus('idle');
            }
          }
        };
      } else {
        toast({
          title: 'Speech Recognition Not Supported',
          description: 'Your browser does not support speech recognition. Please use Chrome, Edge, or Safari.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    initializeSpeechRecognition();

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []); // Only run once on mount

  // Test microphone permissions
  const testMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());

      toast({
        title: 'Microphone Access Granted',
        description: 'Microphone is working. You can now use speech recognition.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return true;
    } catch (error) {
      console.error('Microphone access error:', error);
      toast({
        title: 'Microphone Access Denied',
        description: 'Please allow microphone access to use speech recognition.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  };

  const startRecording = async () => {
    console.log('Starting recording...');

    // Test microphone access first
    const microphoneAccess = await testMicrophone();
    if (!microphoneAccess) {
      return;
    }

    if (!recognitionRef.current) {
      toast({
        title: 'Speech Recognition Not Available',
        description: 'Speech recognition is not initialized.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (isListeningRef.current) {
      console.log('Already listening, stopping first...');
      recognitionRef.current.stop();
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    try {
      setTranscript('');
      setFinalTranscript('');
      setIsRecording(true);
      setStatus('listening');

      console.log('Starting speech recognition...');
      recognitionRef.current.start();

      toast({
        title: 'Recording Started',
        description: 'Speak now, your voice will be transcribed in real-time.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setIsRecording(false);
      setStatus('idle');

      toast({
        title: 'Failed to Start Recording',
        description: 'Could not start speech recognition. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const stopRecording = () => {
    console.log('Stopping recording...');

    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
    setStatus('idle');
    isListeningRef.current = false;

    // Combine final transcript with any remaining interim transcript
    const currentTranscript = (finalTranscript + transcript).trim();

    // If there was paused content, append new content to it
    const completeTranscript = pausedTranscript
      ? `${pausedTranscript} ${currentTranscript}`.trim()
      : currentTranscript;

    console.log('Complete transcript:', completeTranscript);

    if (completeTranscript) {
      if (type === 'text') {
        setData({ ...data, text: completeTranscript });
      }

      // Update final transcript with complete content
      setFinalTranscript(completeTranscript);
      setTranscript('');

      // Clear paused content and pause state
      setPausedTranscript('');
      setIsPaused(false);
      setIsEditing(true);

      toast({
        title: 'Recording Stopped',
        description: 'Transcription complete. You can now edit the text.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'No Speech Detected',
        description: 'No speech was detected. Please try speaking again.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const pauseRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsPaused(true);
      setIsRecording(false);

      // Save current transcript as paused content
      const currentContent = (finalTranscript + transcript).trim();
      setPausedTranscript(currentContent);

      toast({
        title: 'Recording Paused',
        description: 'Recording paused. Click resume to continue.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const resumeRecording = () => {
    if (isPaused) {
      setIsPaused(false);
      setTranscript('');
      startRecording();

      toast({
        title: 'Recording Resumed',
        description: 'Recording resumed. Continue speaking.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleDelete = () => {
    setTranscript('');
    setFinalTranscript('');
    if (type === 'text') {
      setData({ ...data, text: '' });
    }
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(!isEditing);
  };

  const renderparametersContent = () => (
    <Grid templateColumns="repeat(6, 1fr)" gap={4} flexGrow={1}>
      {[
        { label: 'BP', value: data.bp || '--/--', unit: 'mmHg' },
        { label: 'Pulse', value: data.pulse || '--', unit: 'bpm' },
        { label: 'Weight', value: data.weight || '--', unit: 'kg' },
        { label: 'SpO2', value: data.spo2 || '--', unit: '%' },
        { label: 'Height', value: data.height || '--', unit: 'cm' },
        { label: 'Temperature', value: data.temperature || '--', unit: '°F' },
      ].map((item, index) => (
        <GridItem key={index}>
          <Box bg={cardBg} rounded="md" p={3} textAlign="center" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Text fontSize="sm" color="gray.500" mb={1}>{item.label}</Text>
            <Text fontSize="lg" fontWeight="medium">{item.value}</Text>
            <Text fontSize="xs" color="gray.400">{item.unit}</Text>
          </Box>
        </GridItem>
      ))}
    </Grid>
  );

  const renderTextContent = () => {
    const currentTranscript = finalTranscript + transcript;
    const hasTranscript = currentTranscript.trim().length > 0 || (data.text && data.text.trim().length > 0);

    console.log('=== RENDER STATE ===', {
      isRecording,
      hasTranscript,
      currentTranscript: `"${currentTranscript}"`,
      dataText: `"${data.text}"`,
      finalTranscript: `"${finalTranscript}"`,
      transcript: `"${transcript}"`,
      isEditing,
      shouldShowRecording: isRecording,
      shouldShowTranscript: !isRecording && (hasTranscript || isEditing),
      shouldShowDefault: !isRecording && !hasTranscript && !hideTextarea
    });

    // Render different content based on state
    if (isRecording) {
      return (
        <Box
          key="recording"
          bg={cardBg}
          rounded="md"
          p={6}
          minH="120px"
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="sm"
          position="relative"
          display="flex"
          flexDirection="column"
          alignItems="stretch"
        >
          <Box
            bg="blue.50"
            border="2px solid"
            borderColor="blue.200"
            rounded="md"
            p={4}
            minH="100px"
            position="relative"
            w="100%"
          >
            <Text fontSize="sm" color="blue.600" fontWeight="medium" mb={2}>
              🎤 Live Transcription:
            </Text>
            <Text fontSize="md" color="gray.800" lineHeight="1.5" minH="60px">
              <Text as="span" color="gray.900" fontWeight="medium">
                {finalTranscript}
              </Text>
              <Text as="span" color="blue.600" fontStyle="italic">
                {transcript}
              </Text>
              {!currentTranscript && (
                <Text as="span" color="gray.400" fontStyle="italic">
                  Start speaking...
                </Text>
              )}
            </Text>
            <Box
              position="absolute"
              top={2}
              right={2}
              w={3}
              h={3}
              bg="red.500"
              rounded="full"
              animation={`${pulse} 2s infinite`}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box
        key="not-recording"
        bg={cardBg}
        rounded="md"
        p={3}
        minH="100px"
        borderWidth="1px"
        borderColor={borderColor}
        position="relative"
        display="flex"
        flexDirection="column"
        alignItems="stretch"
      >

        {/* Show editable textarea when not recording and has transcript or in edit mode */}
        {!isRecording && (hasTranscript || isEditing) && (
          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Transcribed Text:
              </Text>
              <Flex gap={2}>
                <IconButton
                  icon={<FaEdit />}
                  size="sm"
                  colorScheme="blue"
                  variant="ghost"
                  onClick={handleEdit}
                  aria-label="Edit text"
                  title="Edit text"
                />
                <IconButton
                  icon={<FaTrash />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={handleDelete}
                  aria-label="Delete text"
                  title="Delete text"
                />
              </Flex>
            </Flex>
            <Textarea
              value={data.text || currentTranscript}
              onChange={(e) => setData({ ...data, text: e.target.value })}
              placeholder="Your transcribed text will appear here..."
              size="md"
              rows={4}
              // bg="white"
              borderRadius="md"
              borderColor={borderColor}
              _hover={{ borderColor: 'blue.400' }}
              _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
              resize="vertical"
              readOnly={!isEditing}
              cursor={isEditing ? 'text' : 'default'}
              bg={isEditing ? 'white' : 'gray.50'}
            />
          </Box>
        )}

        {/* Show default textarea when not recording and no transcript */}
        {!hasTranscript && !hideTextarea && (
          <Textarea
            value={data.text || ''}
            onChange={(e) => setData({ ...data, text: e.target.value })}
            placeholder="Enter or speak your text here..."
            size="md"
            rows={4}
            bg="white"
            borderRadius="md"
            borderColor={borderColor}
            _hover={{ borderColor: 'blue.400' }}
            _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
            resize="vertical"
          />
        )}
      </Box>
    );
  };

  return (
    <Box bg={bgColor} rounded="md" borderWidth="1px" borderColor={borderColor} p={4} mb={4} position="relative">
      <Flex justify="space-between" align="center" mb={3} pb={2}>
        <Text fontSize="lg" fontWeight="semibold">{title}</Text>
        <Flex gap={2}>
          <Badge colorScheme="blue" fontSize="xs" px={2} py={1}>VOICE INPUT</Badge>
        </Flex>
      </Flex>

      {/* Render actual content based on type */}
      {type === 'parameters' ? renderparametersContent() : renderTextContent()}

      {/* Show spoken text when stopped or paused */}
      {((!isRecording && (finalTranscript + transcript).trim()) || (isPaused && pausedTranscript)) && (
        <Box
          bg="gray.50"
          rounded="md"
          p={3}
          mb={3}
          borderWidth="1px"
          borderColor="gray.200"
          minH="80px"
        >
          <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={2}>
            {isPaused ? 'Paused Text:' : 'Spoken Text:'}
          </Text>
          <Text fontSize="md" color="gray.800" lineHeight="1.5">
            {isPaused ? pausedTranscript : (finalTranscript + transcript).trim()}
          </Text>
        </Box>
      )}

      <Box mt={2} display="flex" justifyContent="center">
        <Flex align="center" gap={3} flexDirection="column">
          <Flex align="center" gap={3}>
            {/* Main Record/Stop Button */}
            <IconButton
              icon={isRecording ? <FaStop /> : <FaMicrophone />}
              colorScheme={isRecording ? 'red' : isPaused ? 'orange' : 'blue'}
              size="lg"
              isRound
              onClick={isPaused ? resumeRecording : toggleRecording}
              animation={isRecording ? `${pulse} 2s infinite` : 'none'}
              boxShadow="md"
              _hover={{ transform: 'scale(1.05)' }}
              _active={{ transform: 'scale(0.95)' }}
              title={isPaused ? 'Resume Recording' : isRecording ? 'Stop Recording' : 'Start Recording'}
            />

            {/* Pause Button - only show when recording */}
            {isRecording && (
              <IconButton
                icon={<FaPause />}
                colorScheme="yellow"
                size="md"
                isRound
                onClick={pauseRecording}
                boxShadow="md"
                _hover={{ transform: 'scale(1.05)' }}
                _active={{ transform: 'scale(0.95)' }}
                title="Pause Recording"
              />
            )}

            {/* Status indicators */}
            {isRecording && (
              <Flex align="center" color="red.500" fontWeight="medium">
                <Box w={3} h={3} bg="red.500" rounded="full" mr={2} animation={`${pulse} 2s infinite`} />
                Recording... Speak now
              </Flex>
            )}
            {isPaused && (
              <Flex align="center" color="orange.500" fontWeight="medium">
                <Box w={3} h={3} bg="orange.500" rounded="full" mr={2} />
                Paused - Click mic to resume
              </Flex>
            )}
            {!isRecording && !isPaused && (finalTranscript + transcript).trim() && (
              <Flex align="center" color="green.500" fontWeight="medium">
                <Box w={3} h={3} bg="green.500" rounded="full" mr={2} />
                Transcription complete
              </Flex>
            )}
          </Flex>
        </Flex>
      </Box>
      {status === 'listening' && !isRecording && (
        <Box mt={4} py={3} px={4} rounded="md" textAlign="center" bg="blue.50" color="blue.700" borderWidth="1px" borderColor="blue.200">
          Ready to listen for {title.toLowerCase()}... Click the microphone to start.
        </Box>
      )}
    </Box>
  );
};

export default VoiceInputSection; 